import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Tabs,
  Tab,
  LinearProgress,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BusinessIcon from '@mui/icons-material/Business';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { portfolioHighlights, portfolioStats, sectorBreakdown } from '../data/portfolioHighlights';

const Portfolio = () => {
  const [selectedSector, setSelectedSector] = useState('all');

  const handleSectorChange = (event, newValue) => {
    setSelectedSector(newValue);
  };

  const filteredCompanies =
    selectedSector === 'all'
      ? portfolioHighlights
      : portfolioHighlights.filter((company) => company.sector.includes(selectedSector));

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #052d50 0%, #478289 100%)',
          color: 'white',
          py: 8,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Our Portfolio
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, maxWidth: 800, mx: 'auto' }}>
            Discover the innovative Israeli tech companies we've invested in
          </Typography>
        </Container>
      </Box>

      {/* Portfolio Stats */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={3} sx={{ textAlign: 'center' }}>
          <Grid item xs={6} md={2}>
            <Box>
              <Typography variant="h4" sx={{ color: '#478289', fontWeight: 700 }}>
                {portfolioStats.totalCompanies}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Companies
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={2}>
            <Box>
              <Typography variant="h4" sx={{ color: '#478289', fontWeight: 700 }}>
                {portfolioStats.activeInvestments}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Active Investments
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={2}>
            <Box>
              <Typography variant="h4" sx={{ color: '#478289', fontWeight: 700 }}>
                {portfolioStats.successfulExits}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Successful Exits
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={2}>
            <Box>
              <Typography variant="h4" sx={{ color: '#478289', fontWeight: 700 }}>
                {portfolioStats.unicorns}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Unicorns
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={2}>
            <Box>
              <Typography variant="h4" sx={{ color: '#478289', fontWeight: 700 }}>
                {portfolioStats.publicCompanies}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Public Companies
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={2}>
            <Box>
              <Typography variant="h4" sx={{ color: '#478289', fontWeight: 700 }}>
                {portfolioStats.averageGrowth}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Avg. Growth
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Sector Breakdown */}
      <Box sx={{ backgroundColor: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 4 }}>
            Portfolio by Sector
          </Typography>
          <Grid container spacing={3}>
            {sectorBreakdown.map((sector, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {sector.sector}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {sector.count} companies ({sector.percentage}%)
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={sector.percentage}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': { backgroundColor: '#478289', borderRadius: 5 },
                    }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Sector Filter Tabs */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
          <Tabs
            value={selectedSector}
            onChange={handleSectorChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': { fontWeight: 600 },
              '& .Mui-selected': { color: '#478289' },
              '& .MuiTabs-indicator': { backgroundColor: '#478289' },
            }}
          >
            <Tab label="All Companies" value="all" />
            <Tab label="AI & ML" value="AI" />
            <Tab label="FinTech" value="FinTech" />
            <Tab label="HealthTech" value="HealthTech" />
            <Tab label="Automotive" value="Automotive" />
            <Tab label="BioTech" value="BioTech" />
            <Tab label="InsurTech" value="InsurTech" />
          </Tabs>
        </Box>

        {/* Featured Companies */}
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
          Featured Portfolio Companies
        </Typography>

        <Grid container spacing={4}>
          {filteredCompanies.map((company) => (
            <Grid item xs={12} md={6} lg={4} key={company.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s',
                  '&:hover': { transform: 'translateY(-8px)', boxShadow: 6 },
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  {/* Logo and Status */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Avatar src={company.logo} alt={company.name} sx={{ width: 64, height: 64 }} />
                    <Chip
                      label={company.status}
                      size="small"
                      sx={{
                        backgroundColor:
                          company.status === 'Public Company' || company.status === 'Successful Exit'
                            ? '#4caf50'
                            : '#2196f3',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  </Box>

                  {/* Company Info */}
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#052d50' }}>
                    {company.name}
                  </Typography>
                  <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mb: 2 }}>
                    {company.tagline}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                    {company.description}
                  </Typography>

                  {/* Highlight Badge */}
                  <Box sx={{ mb: 3 }}>
                    <Chip
                      icon={
                        company.status === 'Public Company' || company.status === 'Successful Exit' ? (
                          <ExitToAppIcon />
                        ) : (
                          <TrendingUpIcon />
                        )
                      }
                      label={company.highlight}
                      sx={{ backgroundColor: '#e3f2fd', fontWeight: 500 }}
                    />
                  </Box>

                  {/* Key Metrics */}
                  <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1, mb: 2 }}>
                    <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                      KEY METRICS
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Funding:</strong> {company.key_metrics.funding}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Valuation:</strong> {company.key_metrics.valuation}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Achievement:</strong> {company.key_metrics.achievement}
                    </Typography>
                  </Box>

                  {/* Footer Info */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label={company.sector} size="small" variant="outlined" />
                    <Chip label={company.investment_round} size="small" variant="outlined" />
                    <Chip
                      icon={<BusinessIcon sx={{ fontSize: 16 }} />}
                      label={company.location}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #052d50 0%, #478289 100%)',
          color: 'white',
          py: 8,
          mt: 6,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 600 }}>
            Invest in the Next Success Story
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem', opacity: 0.9 }}>
            Join Angels67 to access exclusive investment opportunities in Israeli tech startups
          </Typography>
          <Box
            component="a"
            href="/investment-opportunities"
            sx={{
              display: 'inline-block',
              px: 6,
              py: 2,
              backgroundColor: 'white',
              color: '#052d50',
              borderRadius: 1,
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: '1.1rem',
              '&:hover': { backgroundColor: '#f5f5f5' },
            }}
          >
            View Current Opportunities
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Portfolio;
